import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { Skeleton } from "../components/ui/Skeleton";
import { clientsApi } from "../api/clientsApi";
import { disputesApi } from "../api/disputesApi";
import { disputeLettersApi } from "../api/disputeLettersApi";
import { DisputeItem, DisputeLetter, LetterTemplate } from "../types";
import { printLetterInHiddenIframe } from "../utils/letterPrintWindow";

type DisputeOption = DisputeItem & { clientName: string };

const bureauOptions = ["Equifax", "Experian", "TransUnion", "Creditor / Furnisher"];

const DisputeLetterPage: React.FC = () => {
  const [disputes, setDisputes] = useState<DisputeOption[]>([]);
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [letters, setLetters] = useState<DisputeLetter[]>([]);
  const [selectedDisputeId, setSelectedDisputeId] = useState("");
  const [letterType, setLetterType] = useState("");
  const [bureauTarget, setBureauTarget] = useState(bureauOptions[0]);
  const [letterBody, setLetterBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [loadingDisputes, setLoadingDisputes] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoadingDisputes(true);
      setLoadingTemplates(true);
      setError("");
      try {
        const [clientList, tmpl] = await Promise.all([clientsApi.getAll(), disputeLettersApi.getTemplates()]);
        setTemplates(tmpl);
        if (tmpl.length > 0) {
          setLetterType(tmpl[0].typeKey);
        }

        const rows: DisputeOption[] = [];
        for (const c of clientList) {
          const list = await disputesApi.getAllForClient(c.id);
          for (const d of list) {
            rows.push({ ...d, clientName: `${c.firstName} ${c.lastName}` });
          }
        }
        setDisputes(rows);
        if (rows.length > 0) {
          setSelectedDisputeId((prev) => prev || rows[0].id);
        }
      } catch {
        setError("Could not load disputes or templates.");
      } finally {
        setLoadingDisputes(false);
        setLoadingTemplates(false);
      }
    };
    void load();
  }, []);

  const selectedDispute = useMemo(
    () => disputes.find((d) => d.id === selectedDisputeId) ?? null,
    [disputes, selectedDisputeId]
  );

  useEffect(() => {
    if (!selectedDisputeId) {
      setLetters([]);
      return;
    }
    const loadLetters = async () => {
      try {
        const data = await disputeLettersApi.getLettersForDispute(selectedDisputeId);
        setLetters(data);
      } catch {
        setLetters([]);
      }
    };
    void loadLetters();
  }, [selectedDisputeId]);

  const selectedTemplateName = useMemo(() => {
    const t = templates.find((x) => x.typeKey === letterType);
    return t?.name ?? letterType;
  }, [templates, letterType]);

  const activeTemplate = useMemo(() => templates.find((x) => x.typeKey === letterType), [templates, letterType]);

  const handleGenerate = async () => {
    if (!selectedDisputeId) {
      setError("Select a dispute first.");
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const text = await disputeLettersApi.generateLetter(selectedDisputeId, {
        letterType,
        bureauTarget,
      });
      setLetterBody(text);
      setPreview(false);
    } catch {
      setError("Letter generation failed. Check the API and IntelliWarrior AI configuration.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSent = async () => {
    if (!selectedDisputeId) {
      setError("Select a dispute first.");
      return;
    }
    if (!letterBody.trim()) {
      setError("Generate or enter letter text before saving.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await disputeLettersApi.saveLetter(selectedDisputeId, {
        letterContent: letterBody,
        letterType: selectedTemplateName,
        bureauTarget,
        markAsSent: true,
      });
      const data = await disputeLettersApi.getLettersForDispute(selectedDisputeId);
      setLetters(data);
    } catch {
      setError("Could not save letter.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500 focus:ring-2";
  const labelClass = "block text-xs font-medium uppercase tracking-wide text-slate-400";

  const handlePrintCurrent = () => {
    setError("");
    if (!printLetterInHiddenIframe(letterBody, { title: "Dispute letter" })) {
      setError("Could not prepare the letter for printing.");
    }
  };

  const handleDownloadPdf = () => {
    setError("");
    if (
      !printLetterInHiddenIframe(letterBody, {
        forPdf: true,
        title: "Dispute letter — Save as PDF",
      })
    ) {
      setError("Could not prepare the letter for printing.");
    }
  };

  const handlePrintSaved = (content: string, title: string) => {
    setError("");
    if (!printLetterInHiddenIframe(content, { title })) {
      setError("Could not prepare the letter for printing.");
    }
  };

  const outlineBtnClass =
    "rounded-lg border border-slate-500/60 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800/80 disabled:opacity-50";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Dispute letters</h1>
          <p className="text-sm text-slate-400">
            Generate CROA-aware letters with IntelliWarrior AI, edit, preview, and save when sent.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">{error}</div>
        )}

        {(loadingDisputes || loadingTemplates) && (
          <div className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-6" aria-busy="true" aria-label="Loading letters">
            <div className="grid gap-4 lg:grid-cols-3">
              <Skeleton className="h-56 rounded-lg" />
              <Skeleton className="h-56 rounded-lg lg:col-span-2" />
            </div>
          </div>
        )}

        <div className={`grid gap-6 lg:grid-cols-3 ${loadingDisputes || loadingTemplates ? "hidden" : ""}`}>
          <div className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-5 lg:col-span-1">
            <h2 className="text-sm font-semibold text-blue-300">Letter setup</h2>

            <div>
              <label className={labelClass} htmlFor="dispute">
                Dispute
              </label>
              <select
                id="dispute"
                value={selectedDisputeId}
                onChange={(e) => setSelectedDisputeId(e.target.value)}
                disabled={loadingDisputes || disputes.length === 0}
                className={inputClass}
              >
                {disputes.length === 0 ? (
                  <option value="">No disputes available</option>
                ) : (
                  disputes.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.creditor} — {d.clientName} ({d.accountType})
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className={labelClass} htmlFor="letterType">
                Letter type
              </label>
              <select
                id="letterType"
                value={letterType}
                onChange={(e) => setLetterType(e.target.value)}
                disabled={loadingTemplates || templates.length === 0}
                className={inputClass}
              >
                {templates.map((t) => (
                  <option key={t.typeKey} value={t.typeKey}>
                    {t.name}
                  </option>
                ))}
              </select>
              {activeTemplate && <p className="mt-2 text-xs text-slate-500">{activeTemplate.description}</p>}
            </div>

            <div>
              <label className={labelClass} htmlFor="bureau">
                Bureau / target
              </label>
              <select id="bureau" value={bureauTarget} onChange={(e) => setBureauTarget(e.target.value)} className={inputClass}>
                {bureauOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={() => void handleGenerate()}
                disabled={generating || !selectedDisputeId}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {generating ? "Generating…" : "Generate letter"}
              </button>
              <button
                type="button"
                onClick={() => setPreview((p) => !p)}
                className="rounded-lg border border-blue-500/50 px-4 py-2 text-sm font-medium text-blue-200 hover:bg-blue-950/50"
              >
                {preview ? "Edit mode" : "Preview"}
              </button>
              <button
                type="button"
                onClick={handlePrintCurrent}
                disabled={!letterBody.trim()}
                className={outlineBtnClass}
              >
                Print letter
              </button>
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={!letterBody.trim()}
                className={outlineBtnClass}
              >
                Download as PDF
              </button>
            </div>

            <button
              type="button"
              onClick={() => void handleSaveSent()}
              disabled={saving || !letterBody.trim()}
              className="w-full rounded-lg border border-blue-400/40 bg-blue-950/40 px-4 py-2.5 text-sm font-semibold text-blue-100 hover:bg-blue-900/40 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save and mark as sent"}
            </button>

            {selectedDispute && (
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-xs text-slate-400">
                <p>
                  <span className="text-slate-500">Reason:</span> {selectedDispute.disputeReason || "—"}
                </p>
                <p className="mt-1">
                  <span className="text-slate-500">Strategy:</span> {selectedDispute.disputeStrategy || "—"}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-5 lg:col-span-2">
            <h2 className="text-sm font-semibold text-blue-300">Letter body</h2>
            {preview ? (
              <div className="min-h-[420px] rounded-lg border border-slate-800 bg-slate-950 p-6 font-serif text-sm leading-relaxed text-slate-200 whitespace-pre-wrap shadow-inner">
                {letterBody || <span className="text-slate-600">Nothing to preview yet.</span>}
              </div>
            ) : (
              <textarea
                value={letterBody}
                onChange={(e) => setLetterBody(e.target.value)}
                placeholder="Generate a letter or paste your own draft…"
                rows={18}
                className="min-h-[420px] w-full rounded-lg border border-slate-700 bg-slate-950 px-4 py-3 font-mono text-sm text-slate-100 outline-none ring-blue-500 placeholder:text-slate-600 focus:ring-2"
              />
            )}
          </div>
        </div>

        {!(loadingDisputes || loadingTemplates) && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-sm font-semibold text-blue-300">Saved letters for this dispute</h2>
          {letters.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No letters saved yet for the selected dispute.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {letters.map((l) => (
                <li
                  key={l.id}
                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-white">{l.letterType}</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-500">
                        {new Date(l.createdAt).toLocaleString()} · {l.bureauTarget}
                        {l.sentAt ? " · Sent" : " · Draft"}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handlePrintSaved(
                            l.letterContent,
                            `Dispute letter — ${l.letterType}`
                          )
                        }
                        disabled={!l.letterContent?.trim()}
                        className="rounded border border-slate-600 px-2 py-1 text-xs font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                      >
                        Print
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs text-slate-500">{l.letterContent}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DisputeLetterPage;
